import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ActivityDefinition, Bundle, ChargeItemDefinition } from 'fhir/r4';
import { combineLatest, map, of, switchMap, take } from 'rxjs';
import { backendEndPointToken } from '../app.config';
import { ServiceRequestSelection } from '../shared/service-request-code-picker/service-request-code-picker.component';
import { StateService } from '../shared/state.service';
import { ServiceRequestCodeOptionWithStatus, UtilityService } from '../shared/utility.service';
import { ServiceDetailsComponent } from './service-details.component';

@Component({
    selector: 'app-add-service-details-page',
    standalone: true,
    imports: [CommonModule, ServiceDetailsComponent],
    templateUrl: './add-service-details-page.component.html',
    styleUrl: './add-service-details-page.component.scss'
})
export class AddServiceDetailsPageComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly http = inject(HttpClient);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly stateService = inject(StateService);
    private readonly utilityService = inject(UtilityService);

    serviceCodeAndCategory?: {
        category: ServiceRequestSelection['category'];
        serviceRequestCode: ServiceRequestSelection['serviceRequestCode'];
    };

    existingChargeItemDefinition?: ChargeItemDefinition;
    existingActivityDefinition?: ActivityDefinition;

    loading = true;

    ngOnInit(): void {
        this.route.queryParamMap
            .pipe(
                map((pm) => (pm.get('code') || '').trim()),
                switchMap((code) => {
                    this.loading = true;
                    if (!code) {
                        this.serviceCodeAndCategory = undefined;
                        this.existingChargeItemDefinition = undefined;
                        this.existingActivityDefinition = undefined;
                        this.loading = false;
                        return of(null);
                    }

                    return combineLatest([
                        this.loadChargeItemDefinitions().pipe(map((defs) => defs.find((d) => (d?.code?.coding?.[0]?.code || '') === code) || undefined)),
                        this.loadActivityDefinitions().pipe(map((defs) => defs.find((d) => (d?.code as any)?.coding?.[0]?.code === code) || undefined)),
                        this.utilityService.getServiceRequestCategoryForCode(code).pipe(take(1)),
                    ]).pipe(
                        take(1),
                        map(([chargeDef, activityDef, categoryMeta]) => ({ code, chargeDef, activityDef, categoryMeta }))
                    );
                })
            )
            .subscribe({
                next: (result) => {
                    if (!result) return;

                    const { code, chargeDef, activityDef, categoryMeta } = result;

                    this.existingChargeItemDefinition = chargeDef;
                    this.existingActivityDefinition = activityDef;

                    const display =
                        (activityDef as any)?.code?.coding?.[0]?.display ||
                        chargeDef?.title ||
                        chargeDef?.description ||
                        code;

                    const system =
                        (activityDef as any)?.code?.coding?.[0]?.system ||
                        chargeDef?.code?.coding?.[0]?.system ||
                        this.backendUrl;

                    const serviceRequestCode: ServiceRequestCodeOptionWithStatus = {
                        code,
                        display: String(display || code),
                        system: String(system || ''),
                        active: true,
                    };

                    const category = categoryMeta
                        ? {
                            code: categoryMeta.categoryCode,
                            display: categoryMeta.categoryDisplay || categoryMeta.categoryCode,
                            system: categoryMeta.categorySystem || '',
                        }
                        : null;

                    this.serviceCodeAndCategory = {
                        category,
                        serviceRequestCode,
                    };

                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                }
            });
    }

    private loadChargeItemDefinitions() {
        return this.stateService.orgWideResources.chargeItemDefinitions.pipe(
            take(1),
            switchMap((defs) => {
                if (defs && defs.length) {
                    return of(defs.map((d) => d.actualResource));
                }

                return this.http.get<Bundle>(`${this.backendUrl}/ChargeItemDefinition?status=active&_count=500`).pipe(
                    take(1),
                    map((bundle) => (bundle.entry || []).map((e: any) => e.resource as ChargeItemDefinition)),
                    map((resources) => {
                        this.stateService.orgWideResources.chargeItemDefinitions.next(
                            resources.map((d) => ({
                                actualResource: d,
                                referenceId: d?.id ? `ChargeItemDefinition/${d.id}` : null,
                                savedStatus: 'saved',
                            }))
                        );
                        return resources;
                    })
                );
            })
        );
    }

    private loadActivityDefinitions() {
        return this.stateService.orgWideResources.activityDefinitions.pipe(
            take(1),
            switchMap((defs) => {
                if (defs && defs.length) {
                    return of(defs.map((d) => d.actualResource));
                }

                return this.http.get<Bundle>(`${this.backendUrl}/ActivityDefinition?status=active&_count=500`).pipe(
                    take(1),
                    map((bundle) => (bundle.entry || []).map((e: any) => e.resource as ActivityDefinition)),
                    map((resources) => {
                        this.stateService.orgWideResources.activityDefinitions.next(
                            resources.map((d) => ({
                                actualResource: d,
                                referenceId: d?.id ? `ActivityDefinition/${d.id}` : null,
                                savedStatus: 'saved',
                            }))
                        );
                        return resources;
                    })
                );
            })
        );
    }
}
